import type { User } from '../types';

/**
 * Utility functions for role-based access control
 */

/**
 * Check if a user has a specific role
 */
export const hasRole = (user: User | null, role: string): boolean => {
  if (!user) return false;
  
  // Check new roles array system
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(role);
  }
  
  // Fallback to legacy single role system
  return user.role === role;
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (user: User | null, roles: string[]): boolean => {
  if (!user || roles.length === 0) return false;
  
  return roles.some(role => hasRole(user, role));
};

/**
 * Check if a user has all of the specified roles
 */
export const hasAllRoles = (user: User | null, roles: string[]): boolean => {
  if (!user || roles.length === 0) return false;
  
  return roles.every(role => hasRole(user, role));
};

/**
 * Get all roles for a user as an array
 */
export const getUserRoles = (user: User | null): string[] => {
  if (!user) return [];
  
  // Check new roles array system
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles;
  }
  
  // Fallback to legacy single role system
  if (user.role) {
    return [user.role];
  }
  
  return [];
};

/**
 * Format user roles for display (capitalized and comma-separated)
 */
export const formatUserRoles = (user: User | null): string => {
  const roles = getUserRoles(user);
  
  if (roles.length === 0) return 'User';
  
  return roles
    .map(role => role.charAt(0).toUpperCase() + role.slice(1))
    .join(', ');
};

/**
 * Check if a user is an admin
 */
export const isAdmin = (user: User | null): boolean => {
  return hasRole(user, 'admin');
};

/**
 * Check if a user can access a specific section based on role requirements
 */
export const canAccessSection = (user: User | null, section: string): boolean => {
  if (!user) return false;
  
  switch (section.toLowerCase()) {
    case 'comms':
    case 'communications':
      return hasAnyRole(user, ['admin', 'user']); // Allow both admin and user roles
    case 'valuation':
    case 'captables':
      return hasRole(user, 'admin');
    case 'finance':
    case 'profitloss':
      return hasRole(user, 'admin');
    case 'projects':
      return hasAnyRole(user, ['admin', 'user']); // Allow both admin and user roles
    case 'administration':
    case 'users-admin':
      return hasRole(user, 'admin');
    default:
      return true; // Allow access to any other sections by default
  }
};